import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../app/store';
import {
  savePermissions,
  mapRoleToPermissions,
  mapPermissionsToRoles,
} from '../../features/members/memberSlice';
import { FiChevronLeft } from 'react-icons/fi';
import { saveRoles } from '../../features/roles/rolesSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
// @ts-ignore: CSS side-effect import without type declarations
import 'react-toastify/dist/ReactToastify.css';
import PermissionsTable from './PermissionsTable';
import MemberProfile from './MemberProfile';
import ActivityAuditTable from './ActivityAuditTable';
import TempDisableConfirmationModal from './TempDisableConfirmationModal';
import RemoveUserModal from './RemoveUserModal';
import {
  removeMember,
  updateMemberRole,
} from '../../features/members/membersSlice';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';

const actions = ['view', 'create', 'edit', 'delete'];

const FiChevronLeftComponent = FiChevronLeft as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// Type for the API response
type PermissionResponse = {
  memberId: string;
  userId: string;
  email: string;
  displayName: string;
  roleId: string;
  roleName: string;
  status: string;
  addedDate: string;
  lastLoginAt: string | null;
  memberDetails: {
    fullName: string;
    email: string;
    role: string;
  };
  modules: ModulePermission[];
};

type ModulePermission = {
  moduleName: string;
  permissions: Record<string, boolean>;
};

// Map module names from API to display names
const moduleNameMap: Record<string, string> = {
  'automations': 'Automations',
  'campaigns': 'Campaigns',
  'templates': 'Templates',
  'audience': 'Audience',
  'audiences': 'Audiences',
  'analytics': 'Analytics',
  'reports': 'Reports',
  'settings': 'Settings',
  'team': 'Team',
  'activity': 'Activity',
  'admin': 'Admin',
  'identity': 'Identity',
  'kafka': 'Kafka',
  'notifications': 'Notifications',
  'org': 'Organization',
  'profile': 'Profile',
  'sender_profiles': 'Sender Profiles',
  'tenant': 'Tenant'
};

// Map permission keys from API to action names
const permissionKeyMap: Record<string, string> = {
  'VIEW': 'view',
  'CREATE': 'create',
  'EDIT': 'edit',
  'DELETE': 'delete',
  'INVITE': 'invite',
  'MEMBERS_MANAGE': 'membersManage',
  'ROLES_MANAGE': 'rolesManage',
  'OWNERSHIP_TRANSFER': 'ownershipTransfer',
  'OWNERSHIP_REQUEST': 'ownershipRequest',
  'DUPLICATE': 'duplicate',
  'SEND_TEST': 'sendTest'
};

// Map Redux action keys to permission keys
const reduxActionToPermissionKey: Record<string, string> = {
  'view': 'VIEW',
  'create': 'CREATE',
  'edit': 'EDIT',
  'delete': 'DELETE'
};

// Map module display names to API module names
const displayToModuleNameMap: Record<string, string> = {
  'Automations': 'automations',
  'Campaigns': 'campaigns',
  'Templates': 'templates',
  'Audience': 'audience',
  'Audiences': 'audiences',
  'Analytics': 'analytics',
  'Reports': 'reports',
  'Settings': 'settings',
  'Team': 'team',
  'Activity': 'activity',
  'Admin': 'admin',
  'Identity': 'identity',
  'Kafka': 'kafka',
  'Notifications': 'notifications',
  'Organization': 'org',
  'Profile': 'profile',
  'Sender Profiles': 'sender_profiles',
  'Tenant': 'tenant'
};

const MemberDetails: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const savedMatrix = useSelector((state: RootState) => state.member);
  const roleMatrix = useSelector((state: RootState) => state.roles);

  const [matrix, setMatrix] = useState(savedMatrix);
  const [hasChanges, setHasChanges] = useState(false);
  const initialMatrixRef = useRef(savedMatrix);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [isDisabled, setIsDisabled] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const location = useLocation();
  const locationState = location.state || {};


  const { role, ownerRole, member, globalIndex, memberId: passedMemberId } = locationState as {
    role: string;
    ownerRole: string;
    member: {
      name: string;
      email: string;
      role: string;
      addedDate: string;
      invitedBy: string;
      lastLogin: string;
      status: string;
      avatar?: string;
      memberId?: string;
    };
    globalIndex?: any;
    memberId?: string;
  };

  // Use memberId from state, or try to get it from the member object
  const memberId = passedMemberId || member?.memberId;

  // Store the roleId and roleName from the API response
  const [currentRoleId, setCurrentRoleId] = useState<string | null>(null);
  const [currentRoleName, setCurrentRoleName] = useState<string | null>(null);

  // Fetch member permissions from API
  useEffect(() => {

    if (memberId) {
      fetchMemberPermissions();
    } else {
      console.warn('⚠️ No memberId provided, using fallback initialization');
      // Fallback to local state if no memberId
      initializeMatrixFromRole();
      setIsLoading(false);
    }
  }, [memberId, role]);

  const fetchMemberPermissions = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const authContext = getAuthContext();


      const { token, tenantId, userId } = authContext;
      if (!token || !tenantId) {
        // console.error('❌ Missing auth credentials');
        toast.error('Authentication required. Please log in again.');
        throw new Error('Authentication required');
      }

      const url = `${BASE_URL}/v1/team/members/${memberId}/permissions`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });


      if (!response.ok) {
        const errorText = await response.text();
        // console.error('❌ API Error response:', errorText);
        if (response.status === 403) {
          throw new Error('You do not have permission to view member permissions');
        }
        throw new Error(`Failed to fetch permissions: ${response.status} - ${errorText}`);
      }

      const data: PermissionResponse = await response.json();

      // Store the roleId and roleName from the response
      setCurrentRoleId(data.roleId);
      setCurrentRoleName(data.roleName);

      initializeMatrixFromAPI(data);
    } catch (err) {
      // console.error('❌ Error in fetchMemberPermissions:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to fetch permissions');
      setError(err instanceof Error ? err.message : 'Failed to fetch permissions');
      // Fallback to role-based initialization
      initializeMatrixFromRole();
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize matrix from API response
  const initializeMatrixFromAPI = (data: PermissionResponse) => {
    const defaultMatrix: Record<string, { view: boolean; create: boolean; edit: boolean; delete: boolean }> = {};

    // Initialize all modules with default false values
    const allModules = ['Automations', 'Campaigns', 'Templates', 'Audience', 'Audiences',
      'Analytics', 'Reports', 'Settings', 'Team', 'Activity', 'Admin', 'Identity',
      'Kafka', 'Notifications', 'Organization', 'Profile', 'Sender Profiles', 'Tenant'];

    allModules.forEach(moduleName => {
      defaultMatrix[moduleName] = {
        view: false,
        create: false,
        edit: false,
        delete: false
      };
    });

    // Map API modules to our matrix
    data.modules.forEach((module) => {
      const displayName = moduleNameMap[module.moduleName] || module.moduleName;

      if (defaultMatrix[displayName]) {
        // Map permissions from API to our actions
        Object.entries(module.permissions).forEach(([key, value]) => {
          const actionKey = permissionKeyMap[key];
          // Only map view, create, edit, delete actions
          if (actionKey && ['view', 'create', 'edit', 'delete'].includes(actionKey)) {
            defaultMatrix[displayName][actionKey as 'view' | 'create' | 'edit' | 'delete'] = value;
          }
        });
      }
    });

    setMatrix(defaultMatrix);
    initialMatrixRef.current = defaultMatrix;
    setHasChanges(false); // Reset changes after initialization
  };

  // Fallback: Initialize matrix from role (existing logic)
  const initializeMatrixFromRole = () => {
    if (role) {
      const mapped = mapRoleToPermissions(role, roleMatrix);
      setMatrix(mapped);
      initialMatrixRef.current = mapped;
      setHasChanges(false);
    }
  };

  // Detects changes in the permission matrix - THIS IS KEY FOR ENABLING THE SAVE BUTTON
  useEffect(() => {
    const changed = Object.keys(matrix).some((module) =>
      actions.some(
        (action) =>
          matrix[module][action as 'view' | 'create' | 'edit' | 'delete'] !==
          initialMatrixRef.current[module]?.[action as 'view' | 'create' | 'edit' | 'delete']
      )
    );
    setHasChanges(changed);
  }, [matrix]);

  // Convert matrix to API format
  const convertMatrixToApiFormat = (matrixData: Record<string, { view: boolean; create: boolean; edit: boolean; delete: boolean }>) => {
    // Group permissions by module
    const modulePermissions: Record<string, Record<string, boolean>> = {};

    Object.entries(matrixData).forEach(([displayName, perms]) => {
      const moduleName = displayToModuleNameMap[displayName] || displayName.toLowerCase();
      modulePermissions[moduleName] = {};

      // Convert view, create, edit, delete to API format
      Object.entries(perms).forEach(([action, value]) => {
        const apiKey = reduxActionToPermissionKey[action];
        if (apiKey) {
          modulePermissions[moduleName][apiKey] = value;
        }
      });
    });

    return modulePermissions;
  };

  // Toggles a permission for a specific module and action
  const togglePermission = (module: string, action: string) => {
    setMatrix((prev) => ({
      ...prev,
      [module]: {
        ...prev[module],
        [action]:
          !prev[module][action as 'view' | 'create' | 'edit' | 'delete'],
      },
    }));
  };

  // Reset function
  const handleReset = () => {
    setMatrix(initialMatrixRef.current);
  };

  // Saves updated permissions by calling the API
  const handleSave = async () => {
    if (!currentRoleId) {
      toast.error('Role ID not found. Please refresh and try again.');
      return;
    }

    if (!currentRoleName) {
      toast.error('Role name not found. Please refresh and try again.');
      return;
    }

    setIsSaving(true);

    try {
      const authContext = getAuthContext();
      const { token, tenantId, userId } = authContext;

      if (!token || !tenantId) {
        throw new Error('Authentication required');
      }

      // Convert matrix to API format
      const modulePermissions = convertMatrixToApiFormat(matrix);

      // Build the permissions array for the API
      const permissions: string[] = [];

      // Map permissions from matrix to API permission strings
      Object.entries(modulePermissions).forEach(([moduleName, perms]) => {
        Object.entries(perms).forEach(([action, hasPermission]) => {
          if (hasPermission) {
            // Convert moduleName and action to API permission string
            // Example: "campaigns.CREATE", "automations.VIEW", etc.
            const permissionString = `${moduleName}.${action}`;
            permissions.push(permissionString);
          }
        });
      });

      const url = `${BASE_URL}/v1/team/roles/${currentRoleId}`;

      const body = {
        permissions: permissions,
        name: currentRoleName
      };


      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update role: ${response.status} - ${errorText}`);
      }

      // After successful save, update the baseline
      const snapshot = JSON.parse(JSON.stringify(matrix));
      initialMatrixRef.current = snapshot;
      setHasChanges(false);

      // Dispatch to Redux
      dispatch(savePermissions(matrix));
      const updatedRoleMatrix = mapPermissionsToRoles(
        role,
        matrix,
        roleMatrix
      );
      dispatch(saveRoles(updatedRoleMatrix));

      toast.success('Permissions saved successfully!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      // console.error('❌ Error saving permissions:', error);
      // toast.error(error instanceof Error ? error.message : 'Failed to save permissions', {
      //   position: 'top-right',
      //   autoClose: 5000,
      //   hideProgressBar: false,
      //   closeOnClick: true,
      //   pauseOnHover: true,
      //   draggable: true,
      // });
    } finally {
      setIsSaving(false);
    }
  };

  // Checks if permissions can be edited based on ownerRole
  const canEdit = ownerRole === 'Admin' || ownerRole === 'Owner';

  // Resets permissions when the member is disabled
  useEffect(() => {
    if (isDisabled) {
      handleReset();
    }
  }, [isDisabled]);

  // Navigates back to the team page
  const handleBack = () => {
    navigate('/team');
  };

  // Handles member removal and redirects to team page
  const handleRemoveConfirm = () => {
    dispatch(removeMember(globalIndex));
    setShowRemoveModal(false);
    navigate('/team');
    setTimeout(() => {
      toast.success('User removed successfully', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }, 500);
  };

  // Handles temporary disable confirmation
  const handleDisableConfirm = () => {
    setShowDisableModal(false);
    setIsDisabled(true);
    toast.warning(`${member.name} has been temporarily disabled`);
  };

  // Handles temporary disable cancellation
  const handleDisableCancel = () => {
    setShowDisableModal(false);
    setIsDisabled(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00A4BD]" />
        <span className="ml-2 text-gray-500">Loading permissions...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md m-4">
        <p className="font-medium">Error loading permissions</p>
        <p className="text-sm">{error}</p>
        <button
          onClick={fetchMemberPermissions}
          className="mt-2 text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className='p-2'>
      <button
        onClick={handleBack}
        className='flex items-center text-sm text-teal-600 mb-4 hover:underline font-semibold'
        style={{ color: '#00899E', fontSize: '14px' }}
      >
        <FiChevronLeftComponent className='mr-1' /> Back to Team
      </button>

      <div>
        <h2 className='text-[20px] font-bold text-[#3F5265] mb-2'>
          Member Details
        </h2>
        <p className='text-[#657584] mb-6 text-[16px]'>
          Control what this member can do — update roles and fine-tune their
          permissions.
        </p>
      </div>

      {/* --- (1) Member Info Card --- */}
      <MemberProfile
        member={member}
        onToggleDisable={(val) => {
          if (val) {
            setShowDisableModal(true);
          } else {
            setIsDisabled(false);
            toast.success(`${member.name} has been re-enabled`);
          }
        }}
        isDisabled={isDisabled}
        ownerRole={ownerRole}
        onRemoveUsermodal={() => setShowRemoveModal(true)}
        onRoleChange={(newRole) => {
          dispatch(updateMemberRole({ index: globalIndex, newRole }));
          toast.success(`${member.name}'s role updated to ${newRole}`);
        }}
      />

      {/* permission granted */}
      <div className='bg-white rounded-md border border-gray-200 shadow-sm mt-6'>
        <div className='flex justify-between items-center px-6 pt-4'>
          <div className='flex space-x-6'>
            <h2 className='text-[20px] font-semibold text-[#3F5265]'>
              Permissions Granted
            </h2>
          </div>
          <div
            className={`flex items-center gap-3 ${ownerRole !== 'Admin' && ownerRole !== 'Owner' ? 'invisible' : ''
              }`}
          >
            <button
              onClick={handleReset}
              className='px-4 py-2 rounded border-[1px] border-[#00A4BD] text-[#00A4BD] bg-white 
             disabled:text-gray-300 disabled:border-gray-300'
              disabled={!hasChanges || isSaving}
            >
              Reset
            </button>
            <button
              onClick={handleSave}
              className='px-4 py-2 rounded bg-[#00A4BD] text-white disabled:bg-gray-300'
              disabled={!hasChanges || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        <div className='px-6 pb-6'>
          <PermissionsTable
            isDisabled={isDisabled || isSaving}
            matrix={matrix}
            actions={actions}
            canEdit={canEdit}
            togglePermission={togglePermission}
            isCheckboxDisabled={() => false}
          />
        </div>
        <ToastContainer position='top-right' autoClose={3000} />
      </div>

      {/* --- (3) Activity & Audit Logs --- */}
      <ActivityAuditTable
        memberId={memberId}
      />
      <TempDisableConfirmationModal
        isOpen={showDisableModal}
        memberName={member?.name || 'this member'}
        onCancel={handleDisableCancel}
        onConfirm={handleDisableConfirm}
      />
      <RemoveUserModal
        isOpen={showRemoveModal}
        memberName={member?.name || 'this member'}
        onCancel={() => setShowRemoveModal(false)}
        onConfirm={handleRemoveConfirm}
      />
    </div>
  );
};

export default MemberDetails;